import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import ProjectDetailDesktopView from 'components/ProjectDetailDesktopView';
import ProjectDetailSheet from 'components/ProjectDetailSheet';
import UnitsBreakdownDialog from 'components/UnitsBreakdownDialog';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { findActionIn } from 'actions';
import { useGetDLProject } from 'features/projects/useGetDLProject';
import { useEditDLProject } from 'features/projects/useEditDLProject';
import { useRemoveDLProject } from 'features/projects/useRemoveDLProject';
import { projectStatusId, } from 'features/projects/types';
/** Per-record unit-list action behind each metric card. */
const UNIT_ACTION_NAMES = {
    total: 'listUnits',
    available: 'listAvailableUnits',
    sold: 'listSoldUnits',
};
function toFormValues(project) {
    return {
        name: project.name || '',
        address: project.address || '',
        description: project.description || '',
        // Owner records carry `agencyNotes`, Agent records carry private `agentNotes` —
        // never both. The form keeps one generic notes value either way.
        notes: project.agentNotes ?? project.agencyNotes ?? '',
        status: projectStatusId(project),
    };
}
export default function ProjectDetailWorkspace({ projectId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const query = useGetDLProject({ projectId });
    const editProject = useEditDLProject();
    const removeProject = useRemoveDLProject();
    const [unitsMetric, setUnitsMetric] = useState(null);
    const goBack = () => void navigate({ to: '/projects' });
    const initial = useMemo(() => {
        if (!query.data)
            return null;
        return toFormValues(query.data);
    }, [query.data]);
    // Always render the metric cards (per Figma layout); empty values display as "—".
    const metrics = useMemo(() => {
        if (!query.data)
            return null;
        return {
            total: query.data.TDSKUnitsTotal ?? null,
            available: query.data.TDSKUnitsAvailable ?? null,
            sold: query.data.TDSKUnitsSold ?? null,
        };
    }, [query.data]);
    const editAction = query.data ? findActionIn(query.data.potentialAction, 'edit') : null;
    const canEdit = Boolean(editAction);
    const canRemove = Boolean(findActionIn(query.data?.potentialAction, 'remove'));
    // A metric card is clickable iff its unit-list action exists on the record
    // (3DSK-linked projects only; present for both personas on dev).
    const clickableMetrics = useMemo(() => {
        const record = query.data;
        const result = {};
        for (const metric of Object.keys(UNIT_ACTION_NAMES)) {
            result[metric] = Boolean(record && findActionIn(record.potentialAction, UNIT_ACTION_NAMES[metric]));
        }
        return result;
    }, [query.data]);
    const unitsAction = unitsMetric
        ? findActionIn(query.data?.potentialAction, UNIT_ACTION_NAMES[unitsMetric])
        : null;
    const handleSave = (next) => {
        if (!query.data || !editAction)
            return;
        editProject.mutate({
            project: query.data,
            edits: {
                // Only include fields whose corresponding *-input exists on the edit action.
                ...(editAction['name-input'] ? { name: next.name } : {}),
                ...(editAction['address-input'] ? { address: next.address } : {}),
                ...(editAction['description-input'] ? { description: next.description } : {}),
                ...(editAction['status-input'] ? { status: next.status } : {}),
                ...(editAction['agencyNotes-input'] ? { agencyNotes: next.notes } : {}),
                ...(editAction['agentNotes-input'] ? { agentNotes: next.notes } : {}),
            },
        });
    };
    const handleDelete = () => {
        if (!query.data)
            return;
        removeProject.mutate({ project: query.data }, { onSuccess: () => goBack() });
    };
    if (query.isPending) {
        return (_jsx("p", { className: "m-0 px-2 py-6 text-center text-[14px] leading-5 text-blacktext-080", children: t('projects_loading') }));
    }
    if (query.isError || !initial) {
        return (_jsx("p", { className: "m-0 px-2 py-6 text-center text-[14px] leading-5 text-error", children: t('projects_load_error') }));
    }
    const unitsDialog = (_jsx(UnitsBreakdownDialog, { open: Boolean(unitsMetric && unitsAction), onOpenChange: (next) => {
            if (!next)
                setUnitsMetric(null);
        }, action: unitsAction, actionName: unitsMetric ? UNIT_ACTION_NAMES[unitsMetric] : '', project: query.data ?? null }));
    if (isDesktop) {
        return (_jsxs(_Fragment, { children: [_jsx(ProjectDetailDesktopView, { project: query.data, initial: initial, metrics: metrics, onBack: goBack, onSave: handleSave, canEdit: canEdit, editAction: editAction, onDelete: handleDelete, isDeleting: removeProject.isPending, canRemove: canRemove, isSaving: editProject.isPending, clickableMetrics: clickableMetrics, onMetricClick: setUnitsMetric }), unitsDialog] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ProjectDetailSheet, { open: true, project: query.data, initial: initial, metrics: metrics, onOpenChange: (next) => {
                    if (!next)
                        goBack();
                }, onSave: handleSave, canEdit: canEdit, editAction: editAction, onDelete: handleDelete, isDeleting: removeProject.isPending, canRemove: canRemove, isSaving: editProject.isPending, clickableMetrics: clickableMetrics, onMetricClick: setUnitsMetric }), unitsDialog] }));
}
